import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import React, { useMemo, useState } from "react";
import { EntityDragRow } from "../../dnd/EntityDragRow";
import { BaseSelectorProps } from "../BaseSelector";

import { Button } from "@/components/ui/button";
import { cn } from "@/utils";
import {
  CalendarEventCard,
  CalendarEventCardSkeleton,
} from "@cohesive/ui-components";
import { addDays, format, subDays } from "date-fns";
import { buildGenericById, toGenericArray } from "../../genericProjection";
import { Selector, SelectorResults, SelectorSidebar } from "../Selector";
import {
  getEventsForDay,
  getWeekDaysAround,
  positionEventsInDay,
} from "./types";
import { useOutlookCalendarEventsList } from "./useOutlookCalendarEventsList";

export const OutlookCalendarSelector: React.FC<BaseSelectorProps> = ({
  connection,
  onSelect,
}) => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  // Fetch events for selected date range using filter
  const { data: eventsData, isLoading: isEventsLoading } =
    useOutlookCalendarEventsList(connection, {
      filter: `start/dateTime ge '${format(subDays(selectedDate, 30), "yyyy-MM-dd'T'HH:mm:ss")}' and end/dateTime le '${format(addDays(selectedDate, 30), "yyyy-MM-dd'T'HH:mm:ss")}'`,
      orderBy: "start/dateTime asc",
      maxResults: 100,
    });

  // Generic projections (entity_format=both) keyed by entity id — placed on the
  // canvas in place of the typed calendar event.
  const genericById = useMemo(
    () =>
      buildGenericById(
        eventsData?.pages.flatMap((page) =>
          toGenericArray(page.data_generic),
        ) ?? [],
      ),
    [eventsData],
  );

  // Plain-click placement: EntityDragRow already forwards the generic here.
  const handleEventSelect = (event: any) => {
    onSelect(event);
  };

  // Mention-click placement bypasses EntityDragRow, so resolve the generic
  // from the typed event ourselves before placing it on the canvas.
  const handleEventMentionClick = (event: any) => {
    onSelect(genericById.get(event.data.id) ?? event);
  };

  // Get all events from Toolbox action response
  const allEvents = useMemo(() => {
    if (!eventsData?.pages) return [];

    return eventsData.pages.flatMap(
      (page) =>
        page.data?.map((entity: any) => ({
          platform: entity.platform,
          base: entity.base,
          type: entity.type,
          data: {
            id: entity.data.id,
            summary: entity.data.summary || entity.data.title,
            start_date_time: entity.data.start_date_time,
            end_date_time: entity.data.end_date_time,
            start_time_zone: entity.data.start_time_zone,
            end_time_zone: entity.data.end_time_zone,
            location: entity.data.location,
            attendees: entity.data.attendees,
            status: entity.data.status,
            html_link: entity.data.html_link,
            web_link: entity.data.web_link,
          },
        })) || [],
    );
  }, [eventsData]);

  // Get week days around selected date
  const weekDays = useMemo(() => {
    return getWeekDaysAround(selectedDate);
  }, [selectedDate]);

  // Get events for selected day
  const dayEvents = useMemo(() => {
    return getEventsForDay(allEvents, selectedDate);
  }, [allEvents, selectedDate]);

  // Position events in day view
  const positionedEvents = useMemo(() => {
    return positionEventsInDay(dayEvents);
  }, [dayEvents]);

  // Separate all-day events from timed events
  const { allDayEvents, timedEvents } = useMemo(() => {
    const allDay = positionedEvents.filter((e) => e.isAllDay);
    const timed = positionedEvents.filter((e) => !e.isAllDay);
    return { allDayEvents: allDay, timedEvents: timed };
  }, [positionedEvents]);

  const calendarEventIds = useMemo(
    () => [...allDayEvents, ...timedEvents].map((pe) => pe.data.id),
    [allDayEvents, timedEvents],
  );

  // Day navigation handlers
  const goToPreviousDay = () => setSelectedDate(subDays(selectedDate, 1));
  const goToNextDay = () => setSelectedDate(addDays(selectedDate, 1));
  const goToToday = () => setSelectedDate(new Date());

  const renderDayView = () => {
    return (
      <div className="flex flex-col h-full min-h-0">
        {/* Week navigation */}
        <div className="flex items-center justify-center p-2 border-b bg-muted/50 flex-shrink-0">
          <div className="flex items-center gap-1 flex-wrap">
            {weekDays.map((day) => (
              <Button
                key={day.dateKey}
                variant={day.isSelected ? "default" : "ghost"}
                size="sm"
                className={cn(
                  "text-xs px-2 py-1 h-8 min-w-0",
                  day.isToday && !day.isSelected && "ring-1 ring-rust",
                )}
                onClick={() => setSelectedDate(day.date)}
              >
                {day.shortName}
              </Button>
            ))}
          </div>
        </div>

        {/* Day header with navigation */}
        <div className="flex items-center justify-between p-3 border-b flex-shrink-0 min-h-0">
          <Button variant="ghost" size="sm" onClick={goToPreviousDay}>
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <div className="text-center min-w-0 flex-1">
            <div className="font-medium text-sm sm:text-base">
              {format(selectedDate, "EEEE, MMMM d")}
            </div>
            {!weekDays.find((d) => d.isSelected)?.isToday && (
              <Button
                variant="ghost"
                size="sm"
                onClick={goToToday}
                className="text-xs mt-1"
              >
                Go to today
              </Button>
            )}
          </div>

          <Button variant="ghost" size="sm" onClick={goToNextDay}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* All-day events */}
        {isEventsLoading ? (
          <div className="p-2 sm:p-3 border-b bg-muted/25 max-h-24 sm:max-h-32 overflow-y-auto flex-shrink-0">
            <div className="text-xs font-medium text-muted-foreground mb-1 sm:mb-2">
              All day
            </div>
            <div className="space-y-1">
              <CalendarEventCardSkeleton />
            </div>
          </div>
        ) : (
          allDayEvents.length > 0 && (
            <div className="p-2 sm:p-3 border-b bg-muted/25 max-h-24 sm:max-h-32 overflow-y-auto flex-shrink-0">
              <div className="text-xs font-medium text-muted-foreground mb-1 sm:mb-2">
                All day
              </div>
              <div className="space-y-1">
                {allDayEvents.map((positionedEvent) => (
                  <EntityDragRow
                    key={positionedEvent.data.id}
                    entity={positionedEvent}
                    generic={genericById.get(positionedEvent.data.id)}
                    entityId={positionedEvent.data.id}
                    orderedIds={calendarEventIds}
                    onSelect={handleEventSelect}
                    dragScope="outlook-calendar"
                  >
                    <CalendarEventCard
                      id={positionedEvent.data.id}
                      title={positionedEvent.data.summary}
                      platform="outlook"
                      summary={positionedEvent.data.summary}
                      start_date_time={positionedEvent.data.start_date_time}
                      end_date_time={positionedEvent.data.end_date_time}
                      start_time_zone={positionedEvent.data.start_time_zone}
                      location={positionedEvent.data.location}
                      attendees={positionedEvent.data.attendees}
                      status={positionedEvent.data.status}
                      html_link={positionedEvent.data.html_link}
                      web_link={positionedEvent.data.web_link}
                      onMentionClick={() =>
                        handleEventMentionClick(positionedEvent)
                      }
                      className=""
                    />
                  </EntityDragRow>
                ))}
              </div>
            </div>
          )
        )}

        {/* Timed events */}
        <div className="flex-1 min-h-0 overflow-hidden relative">
          {isEventsLoading ? (
            <div className="absolute inset-0 overflow-y-auto">
              <div className="p-2 sm:p-3 space-y-1 pb-8">
                {[1, 2, 3].map((i) => (
                  <CalendarEventCardSkeleton key={i} />
                ))}
              </div>
            </div>
          ) : timedEvents.length === 0 ? (
            <div className="flex items-center justify-center h-full text-muted-foreground text-sm px-4">
              No events for this day
            </div>
          ) : (
            <div className="absolute inset-0 overflow-y-auto">
              <div className="p-2 sm:p-3 space-y-1 pb-8">
                {timedEvents.map((positionedEvent) => (
                  <EntityDragRow
                    key={positionedEvent.data.id}
                    entity={positionedEvent}
                    generic={genericById.get(positionedEvent.data.id)}
                    entityId={positionedEvent.data.id}
                    orderedIds={calendarEventIds}
                    onSelect={handleEventSelect}
                    dragScope="outlook-calendar"
                  >
                    <CalendarEventCard
                      id={positionedEvent.data.id}
                      title={positionedEvent.data.summary}
                      platform="outlook"
                      summary={positionedEvent.data.summary}
                      start_date_time={positionedEvent.data.start_date_time}
                      end_date_time={positionedEvent.data.end_date_time}
                      start_time_zone={positionedEvent.data.start_time_zone}
                      location={positionedEvent.data.location}
                      attendees={positionedEvent.data.attendees}
                      status={positionedEvent.data.status}
                      html_link={positionedEvent.data.html_link}
                      web_link={positionedEvent.data.web_link}
                      onMentionClick={() =>
                        handleEventMentionClick(positionedEvent)
                      }
                      className=""
                    />
                  </EntityDragRow>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Selector>
      <SelectorSidebar>
        <div className="p-2">
          <div className="flex items-center gap-2 p-2 rounded-lg bg-primary/10">
            <Star className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">Calendar</span>
          </div>
        </div>
      </SelectorSidebar>

      <SelectorResults>{renderDayView()}</SelectorResults>
    </Selector>
  );
};
